/**
 * DESIGN.md §7.2 — Text Link Btn 1.
 * Leading 20px icon + label · gap 8, padding 24×12 · rounded-xl (48px).
 * Hover/click feedback is a SURFACE fill (steel/200); text + icon stay
 * dark. Icon color follows currentColor so `text-text-on-action-2` on
 * the button reaches the SVG via the inner span's text color.
 */
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { externalLinkProps } from '../lib/external-link.js';
import { Icon, type IconName } from './icon.js';

export interface TextLinkBtn1Props extends ComponentProps<'button'> {
  /** Icon name from the §7.1 inventory (default sign-in to match Figma master). */
  icon?: IconName;
  /** Render as an anchor instead of button. */
  href?: string;
  children: ReactNode;
}

const BASE =
  'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl transition-colors ' +
  'font-label font-medium text-label-md text-text-on-action-2 ' +
  'hover:bg-surface-hover-1 active:bg-surface-click-1';

export function TextLinkBtn1({ icon = 'sign-in', href, className, children, ...props }: TextLinkBtn1Props) {
  const content = (
    <>
      {/* No `text-*` class on the Icon — its SVG strokes `currentColor`,
          so it inherits the button's text color and follows overrides
          like `text-primary-white` in the floating nav. */}
      <Icon name={icon} size={20} />
      <span>{children}</span>
    </>
  );

  if (href) {
    return (
      <a href={href} {...externalLinkProps(href)} className={cn(BASE, className)}>
        {content}
      </a>
    );
  }
  return (
    <button type="button" className={cn(BASE, className)} {...props}>
      {content}
    </button>
  );
}
